import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, filter, startWith, Subject } from "rxjs";

import { FreeformId, isFreeformId, PollId, stripPrefix, TypedEntityId } from "../../shared/models/entity.model";
import { TenantResponse } from "../../admin/tenants/models/tenant.model";
import { ThemeResponse } from "../../admin/themes/models/theme.model";
import { InteractiveListItem } from "../../interactives/models/interactives-list.model";

@Injectable()
export class NavbarService {
    private readonly router = inject(Router);

    private readonly _allTenants = signal<TenantResponse[]>([]);
    private readonly _allInteractives = signal<InteractiveListItem[]>([]);
    private readonly _selectedTenantId = signal<string>("");
    private readonly _selectedTheme = signal<ThemeResponse | null>(null);
    private readonly _selectedInteractiveIds$ = new BehaviorSubject<string[]>([]);
    private readonly _refresh$ = new Subject<void>();

    readonly allTenants = this._allTenants.asReadonly();
    readonly allInteractives = this._allInteractives.asReadonly();
    readonly tenantId = this._selectedTenantId.asReadonly();
    readonly selectedTenant = computed(() => this._allTenants().find(t => t.id === this._selectedTenantId()));
    // The selected tenant's theme, loaded once by the layout; null while loading, on failure, or with no theme
    readonly selectedTheme = this._selectedTheme.asReadonly();
    readonly selectedInteractiveIds$ = this._selectedInteractiveIds$.asObservable();
    readonly selectedInteractiveIds = toSignal(this.selectedInteractiveIds$, { requireSync: true });
    readonly refresh$ = this._refresh$.asObservable();

    // Single URL parse per navigation — all derived URL state reads from here.
    private readonly _urlState = signal(this._parseUrl());
    readonly activeView = computed(() => this._urlState().view);
    readonly interactiveId = computed(() => this._urlState().interactiveId);
    readonly typedInteractiveId = computed(() => this._urlState().typedInteractiveId);
    readonly isProducerFreeformView = computed(() => {
        const { view, typedInteractiveId } = this._urlState();
        return view === "producer" && isFreeformId(typedInteractiveId);
    });
    // Multi-select is only offered on the producer freeform dashboard — same route shape as
    // isProducerFreeformView (producer view + a freeform anchor id).
    readonly isMultiSelectInteractive = this.isProducerFreeformView;

    constructor() {
        // On each navigation: reparse the URL and reset the selection to the anchor ID.
        // Multi-select beyond the anchor is tracked only in service state (not the URL),
        // so every navigation resets to the single anchor ID.
        this.router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                startWith(null),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                this._urlState.set(this._parseUrl());
                this._selectedInteractiveIds$.next(this.interactiveId() ? [this.interactiveId()] : []);
            });
    }

    private _parseUrl() {
        const [, , viewSegment, typedIdSegment = ""] = this.router.url.split("/");
        const view = viewSegment === "producer" || viewSegment === "host" ? viewSegment : null;
        return {
            view,
            interactiveId: typedIdSegment ? stripPrefix(typedIdSegment as TypedEntityId) : "",
            typedInteractiveId: typedIdSegment as PollId | FreeformId | ""
        };
    }

    setAllTenants(tenants: TenantResponse[]): void {
        this._allTenants.set(tenants);
    }

    setSelectedTenantId(tenantId: string): void {
        this._selectedTenantId.set(tenantId);
    }

    setSelectedTheme(theme: ThemeResponse | null): void {
        this._selectedTheme.set(theme);
    }

    setAllInteractives(interactives: InteractiveListItem[]): void {
        this._allInteractives.set(interactives);
    }

    setSelectedInteractiveIds(ids: string[]): void {
        this._selectedInteractiveIds$.next(ids);
    }

    triggerRefresh(): void {
        this._refresh$.next();
    }

    reset(): void {
        this._allTenants.set([]);
        this._allInteractives.set([]);
        this._selectedTenantId.set("");
        this.setSelectedTheme(null);
        this._selectedInteractiveIds$.next([]);
        this._urlState.set(this._parseUrl());
    }
}
